// src/client/components/profile-manager/detail-view/TechnicalDataSection.tsx 
// V2.0 Multi-Platform Support

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import ReadOnlyField from './ReadOnlyField';
import type { ProfileWithRelations } from '@shared/types';

interface TechnicalDataSectionProps {
  profile: ProfileWithRelations;
}

/**
 * Bestimmt die Platform(s) basierend auf vorhandenen IDs
 */
function detectPlatforms(profile: ProfileWithRelations): {
  hasLinkedIn: boolean;
  hasXing: boolean;
  label: string;
} {
  const hasLinkedIn = !!profile.linkedinId;
  const hasXing = !!(profile as any).xingId;
  
  let label = 'Unknown';
  if (hasLinkedIn && hasXing) {
    label = 'LinkedIn + XING';
  } else if (hasLinkedIn) {
    label = 'LinkedIn';
  } else if (hasXing) {
    label = 'XING';
  }
  
  return { hasLinkedIn, hasXing, label };
}

export default function TechnicalDataSection({
  profile,
}: TechnicalDataSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const platforms = detectPlatforms(profile);
  const xingId = (profile as any).xingId;
  const xingProfileUrl = (profile as any).xingProfileUrl;

  return (
    <div className="border rounded-lg p-4 bg-white">
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-0 h-auto hover:bg-transparent"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Technical Data</h3>
          {/* Platform Badge(s) */}
          {platforms.hasLinkedIn && (
            <Badge variant="default" className="text-xs">LinkedIn</Badge>
          )}
          {platforms.hasXing && (
            <Badge variant="secondary" className="text-xs">XING</Badge>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5" />
        ) : (
          <ChevronRight className="h-5 w-5" />
        )}
      </Button>
      
      {isExpanded && (
        <div className="mt-4 space-y-0">
          {/* IDs Section */}
          <div className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Identifiers
            </span>
          </div>
          
          <ReadOnlyField
            label="Profile ID"
            value={profile.id}
            copyable={true}
          />
          
          {/* ✅ LinkedIn ID */}
          <ReadOnlyField
            label="LinkedIn ID"
            value={profile.linkedinId}
            copyable={true}
          />
          
          {/* ✅ NEU: XING ID */}
          <ReadOnlyField
            label="XING ID"
            value={xingId}
            copyable={true}
          />
          
          <ReadOnlyField
            label="Profile Hash"
            value={profile.profileUrlHash}
            copyable={true}
          />
          
          {/* URLs Section */}
          <div className="border-t my-3 pt-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              URLs
            </span>
          </div>
          
          {/* LinkedIn URL */}
          <ReadOnlyField
            label="LinkedIn Profile URL"
            value={profile.profileUrl}
            copyable={true}
          />
          
          {/* ✅ NEU: XING URL */}
          <ReadOnlyField
            label="XING Profile URL"
            value={xingProfileUrl}
            copyable={true}
          />
          
          <ReadOnlyField
            label="Image URL"
            value={profile.imageUrl}
            copyable={true}
          />
          
          {/* Company & Metadata Section */}
          <div className="border-t my-3 pt-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Metadata
            </span>
          </div>
          
          <ReadOnlyField
            label="Company Stem"
            value={profile.companyStem}
          />
          
          {/* Timestamps Section */}
          <div className="border-t my-3 pt-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Timestamps
            </span>
          </div>
          
          <ReadOnlyField
            label="Created At"
            value={profile.createdAt}
          />
          
          <ReadOnlyField
            label="Modified At"
            value={profile.modifiedAt}
          />
          
          <ReadOnlyField
            label="Exported At"
            value={profile.exportedAt}
          />
          
          <ReadOnlyField
            label="Validated At"
            value={profile.validatedAt}
          />
          
          {/* Audit Section */}
          <div className="border-t my-3 pt-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Audit
            </span>
          </div>
          
          <ReadOnlyField
            label="Created By"
            value={profile.createdBy}
          />
          
          <ReadOnlyField
            label="Validated By"
            value={profile.validatedBy}
          />
        </div>
      )}
    </div>
  );
}